'use client'

import React, { FC, RefObject } from 'react'
import type { InteractiveButton, PlayerConfig } from '../../../types/interactiveVideo'
import InteractiveVideoPlayer, { type InteractiveVideoPlayerApi } from '../InteractiveVideoPlayer'
import WideBranchOverlay from '../wide/WideBranchOverlay'
import BoxChoicePill from './BoxChoicePill'
import { CHOICE_PILL_BOTTOM, CREDIT_STRIP_HEIGHT, SP_DETAIL_VIDEO_HEIGHT } from './boxConstants'
import BoxWatermark from './BoxWatermark'

export type BoxVideoColumnProps = {
  scenarioId: string
  /** 音ありで再生する設定（BOXはクリック起点なのでミュート解除できる） */
  unmutedConfig: PlayerConfig
  config: PlayerConfig
  /** 動画に重ねる選択肢（分岐ボタン） */
  overlayButtons: InteractiveButton[]
  branchQuestion: string
  isTwoColumn: boolean
  isMobileDetail: boolean
  isChoiceOpen: boolean
  /** 動画を見終わって「決める」フェーズに入っているか */
  isDecisionVisible: boolean
  /** アンケートの描画先（2カラムのときだけ右パネルへ逃がす） */
  surveySlot: HTMLDivElement | null
  playerApiRef: RefObject<InteractiveVideoPlayerApi | null>
  onClose: () => void
  onVideoEnd: (nodeId: string) => void
  onVideoChange: (nodeId: string) => void
  onButtonClick: (button: InteractiveButton) => void
  onTimeUpdate: (currentTime: number, duration: number) => void
  onSurveyGateComplete?: (nodeId: string) => void
  onOpenChoice: () => void
  onCollapseChoice: () => void
  /** 動画の object-fit。タブレット幅の1カラムだけ 'contain'（全体表示）にする */
  videoFit?: 'cover' | 'contain'
}

/**
 * BOX型の動画列。動画のサイズは削らず、下端にシークバーとクレジットを重ねる。
 *
 * 右の動画テキストパネル下部にクレジットを置くと本文が長いときに視線が届かず、
 * 動画の外に帯を作ると動画が小さくなるため、重ねる方式にしている。
 *
 * シークバーは --moovy-seekbar-lift のぶん持ち上げ、空いた足元にクレジットを敷く。
 * 焼き込みのテロップ・出演者カードは端から余白を取って入るので最下端とは競合しない。
 */
const BoxVideoColumn: FC<BoxVideoColumnProps> = ({
  scenarioId,
  unmutedConfig,
  config,
  overlayButtons,
  branchQuestion,
  isTwoColumn,
  isMobileDetail,
  isChoiceOpen,
  isDecisionVisible,
  surveySlot,
  playerApiRef,
  onClose,
  onVideoEnd,
  onVideoChange,
  onButtonClick,
  onTimeUpdate,
  onSurveyGateComplete,
  onOpenChoice,
  onCollapseChoice,
  videoFit,
}) => (
      <div
        style={
          {
            position: 'relative',
            // 縦型(9:16)の幅ちょうど。モーダル高さ × 0.5625 なので左右に余白が出ない。
            width: isTwoColumn ? 'min(51.75vh, 478px)' : isMobileDetail || isChoiceOpen ? 'auto' : '100%',
            height: isTwoColumn ? '100%' : isMobileDetail ? SP_DETAIL_VIDEO_HEIGHT : undefined,
            aspectRatio: !isTwoColumn && (isMobileDetail || isChoiceOpen) ? '9 / 16' : undefined,
            alignSelf: !isTwoColumn && (isMobileDetail || isChoiceOpen) ? 'center' : undefined,
            // 選択肢展開中は、下部の選択肢が使う高さを除いた残りを動画へ渡す。
            // 固定高にすると小さい端末で下に未使用領域が残るため、flex で埋める。
            flex: isTwoColumn || isMobileDetail ? 'none' : isChoiceOpen ? '1 1 0' : 1,
            minHeight: isChoiceOpen ? 0 : undefined,
            flexShrink: 0,
            overflow: 'hidden',
            background: '#020617',
            boxSizing: 'border-box',
            '--moovy-seekbar-lift': `${isTwoColumn ? CREDIT_STRIP_HEIGHT : 0}px`,
          } as React.CSSProperties
        }
      >
        <InteractiveVideoPlayer
          config={unmutedConfig}
          scenarioId={scenarioId}
          videoFit={videoFit}
          containerMode={true}
          hideBackButton={true}
          hideBranchButtons={true}
          // 2カラムのときだけアンケートを右パネルへ逃がす。1カラム（狭い画面）は
          // 動画の下にパネルが積まれる並びなので、従来どおり動画下シートで出す。
          surveyPortalElement={isTwoColumn ? surveySlot : null}
          playerApiRef={playerApiRef}
          onClose={isTwoColumn ? undefined : onClose}
          onVideoEnd={onVideoEnd}
          onVideoChange={onVideoChange}
          onButtonClick={onButtonClick}
          // 残り数秒で選択肢を自動で開く判定（BoxModal.handleTimeUpdate）は PC・SP 共通。
          // SP も終了後に初めて開くのではなく再生中に開く（branchChoiceTiming.ts）。
          onTimeUpdate={onTimeUpdate}
          onSurveyGateComplete={onSurveyGateComplete}
        />

        {/*
         * PCの選択肢。再生開始から重ねたままにすると、動画に焼き込まれたテロップと
         * 出演者クレジットが最初から最後まで隠れるため、再生中はピル1行に畳んでおく。
         * 押せばいつでも開き、残り数秒になったら自動で開く（handleTimeUpdate）。
         * プレイヤー標準の分岐ボタンは非表示のままにして二重描画を防ぐ。
         */}
        {isTwoColumn && overlayButtons.length > 0 && !isChoiceOpen && (
          <BoxChoicePill
            count={overlayButtons.length}
            bottomOffset={CHOICE_PILL_BOTTOM}
            onClick={onOpenChoice}
          />
        )}

        {isTwoColumn && overlayButtons.length > 0 && isChoiceOpen && (
          <WideBranchOverlay
            buttons={overlayButtons}
            questionText={branchQuestion}
            defaultButtonColor={config.defaultButtonColor}
            bottomOffset={CREDIT_STRIP_HEIGHT}
            variant="box"
            // 動画が終わったあとは戻る先が無いので、閉じる導線は再生中だけ出す
            onCollapse={isDecisionVisible ? undefined : onCollapseChoice}
            onSelect={(buttonId) => void playerApiRef.current?.selectButton(buttonId)}
          />
        )}

        {/* PCは映像を最大化するため、クレジットを動画下端へ重ねる。 */}
        {isTwoColumn && <BoxWatermark overlay />}
      </div>
)

export default BoxVideoColumn
